import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { GraphView } from './GraphView';
import { useWasm } from './hooks/useWasm';
import { useSolver } from './hooks/useSolver';
import { InfoPanel } from './components/InfoPanel';
import { CodePanel } from './components/CodePanel';

// Auto-play step intervals in ms for each speed preset.
const PLAY_SPEEDS = { slow: 1000, normal: 350, fast: 80, max: 20 };

function App() {
    // ── Display settings ─────────────────────────────────────────────────────
    const [lightMode,          setLightMode]          = useState(false);
    const [inactiveFrames,     setInactiveFrames]     = useState('show');
    const [showDecisionStack,  setShowDecisionStack]  = useState(false);
    const [showBindings,       setShowBindings]       = useState(false);
    const [showInternalErrors, setShowInternalErrors] = useState(false);
    const [tailCallOpt,        setTailCallOpt]        = useState(false);
    const [showStack,          setShowStack]          = useState(false);
    const [solutionOffset,     setSolutionOffset]     = useState(0);

    // ── Playback state ───────────────────────────────────────────────────────
    const [isPlaying, setIsPlaying] = useState(false);
    const [playSpeed, setPlaySpeed] = useState('normal');

    // ── Editor state ─────────────────────────────────────────────────────────
    const [clauses, setClauses] = useState('');
    const [query,   setQuery]   = useState('');
    const [presets, setPresets] = useState([]);

    // ── Refs ─────────────────────────────────────────────────────────────────
    const clausesEditorRef     = useRef(null);
    const monacoRef            = useRef(null);
    const decorationsRef       = useRef([]);
    const appMainRef           = useRef(null);
    const infoPanelSidebarRef  = useRef(null);

    // ── WASM + solver ────────────────────────────────────────────────────────
    const { moduleRef, ready, status: wasmStatus } = useWasm('/prolog_visualiser.js');
    const {
        snapshots, displayIdx, setDisplayIdx,
        queryKey, clauseError, status: solverStatus,
        idVec, varIdVec, namedVars, clauseRanges,
        display, isAtLatest,
        runQuery, runAll, step,
    } = useSolver(moduleRef);

    const status  = wasmStatus || solverStatus;
    const canStep = ready && display !== null && isAtLatest && !display.atEnd;

    // ── Load presets from public/presets/ ────────────────────────────────────
    useEffect(() => {
        fetch('/presets/index.json')
            .then(r => r.json())
            .then(entries =>
                Promise.all(entries.map(async entry => ({
                    name:    entry.name,
                    clauses: await fetch(`/presets/${entry.clauses}`).then(r => r.text()),
                    query:   (await fetch(`/presets/${entry.query}`).then(r => r.text())).trimEnd(),
                })))
            )
            .then(loaded => {
                setPresets(loaded);
                if (loaded.length) { setClauses(loaded[0].clauses); setQuery(loaded[0].query); }
            })
            .catch(err => console.warn('Could not load presets:', err));
    }, []);

    // ── Light mode — toggle class on <body> so full-page background follows ──
    useEffect(() => {
        document.body.classList.toggle('light-mode', lightMode);
    }, [lightMode]);

    // ── Max width for code panel (cannot overlap info panel) ─────────────────
    const getMaxCodeWidth = useCallback(() => {
        const container = appMainRef.current;
        const infoPanel = infoPanelSidebarRef.current;
        if (!container || !infoPanel) return Infinity;
        const containerRect = container.getBoundingClientRect();
        const infoPanelRect = infoPanel.getBoundingClientRect();
        // Code panel starts at left:10px; leave a 10px gap before the info panel.
        return infoPanelRect.left - containerRect.left - 10 - 10;
    }, []);

    // ── Control handlers ─────────────────────────────────────────────────────

    // Run: (re)initialise the query, stop any active playback.
    const handleRun = useCallback(() => {
        setIsPlaying(false);
        runQuery(clauses, query, tailCallOpt);
    }, [clauses, query, tailCallOpt, runQuery]);

    // Run All: run to completion, stop any active playback.
    const handleRunAll = useCallback(() => {
        setIsPlaying(false);
        runAll(clauses, query, tailCallOpt);
    }, [clauses, query, tailCallOpt, runAll]);

    // Play/Pause: auto-initialise or jump-to-latest as needed, then toggle.
    const handlePlayToggle = useCallback(() => {
        if (isPlaying) {
            setIsPlaying(false);
            return;
        }
        // Reinitialise when there is nothing to play or execution has finished.
        if (!display || (display.atEnd && isAtLatest)) {
            runQuery(clauses, query, tailCallOpt);
        } else if (!isAtLatest) {
            // Jump to the live end of the scrubber so playback can continue.
            setDisplayIdx(snapshots.length - 1);
        }
        setIsPlaying(true);
    }, [isPlaying, display, isAtLatest, snapshots.length, setDisplayIdx,
        clauses, query, tailCallOpt, runQuery]);

    // Step once: auto-initialise if no query has been run yet.
    const handleStep = useCallback(() => {
        if (!display) {
            runQuery(clauses, query, tailCallOpt);
        } else if (canStep) {
            step();
        }
    }, [display, canStep, clauses, query, tailCallOpt, runQuery, step]);

    // ── Auto-step effect ─────────────────────────────────────────────────────
    useEffect(() => {
        if (!isPlaying) return;
        if (!canStep) {
            // Execution finished or user scrubbed away from the live position.
            setIsPlaying(false);
            return;
        }
        const id = setTimeout(step, PLAY_SPEEDS[playSpeed]);
        return () => clearTimeout(id);
    }, [isPlaying, canStep, step, playSpeed, displayIdx]);

    // ── Derived data (memoised for referential stability) ────────────────────

    const choicePointMap = useMemo(() => new Map(
        display?.history.map(dp => [dp.stackIndex, { decisionIndex: dp.decisionIndex, type: dp.type }]) ?? []
    ), [display]);

    const activeChain = useMemo(() => {
        const chain = new Set();
        if (display) {
            let idx = display.stackPointer !== -1 ? display.stackPointer : 0;
            while (idx !== -1) {
                chain.add(idx);
                idx = display.stack[idx]?.parent ?? -1;
            }
        }
        return chain;
    }, [display]);

    // All solution snapshots visible at the current scrubber position.
    const solutionSnaps = useMemo(
        () => snapshots.slice(0, displayIdx + 1).filter(s => s.solutionFound),
        [snapshots, displayIdx]
    );

    // Reset to newest solution whenever a new query starts or the user scrubs.
    useEffect(() => { setSolutionOffset(0); }, [queryKey]);
    useEffect(() => { setSolutionOffset(0); }, [displayIdx]);

    // ── Clause source highlight (Monaco decoration) ───────────────────────────
    useEffect(() => {
        const editor = clausesEditorRef.current;
        const monaco = monacoRef.current;
        if (!editor || !monaco) return;

        const clauseIdx = display?.lastClauseIndex ?? -1;
        if (clauseIdx < 0 || clauseIdx >= clauseRanges.length) {
            decorationsRef.current = editor.deltaDecorations(decorationsRef.current, []);
            return;
        }
        const r = clauseRanges[clauseIdx];
        decorationsRef.current = editor.deltaDecorations(decorationsRef.current, [{
            range: new monaco.Range(r.startLine - 1, 1, r.endLine - 1, 1),
            options: { isWholeLine: true, className: 'clause-highlight' },
        }]);
    }, [display, clauseRanges]);

    // Clear highlight immediately when the user edits the clause text.
    // It re-appears automatically once a new run/step produces a display.
    useEffect(() => {
        const editor = clausesEditorRef.current;
        if (!editor) return;
        decorationsRef.current = editor.deltaDecorations(decorationsRef.current, []);
    }, [clauses]);

    // ── Graph props ───────────────────────────────────────────────────────────
    // When the stack is empty (solution/backtrack step), show the previous frame
    // layout grayed-out rather than a blank graph.
    const isErrorState = display?.solverState?.startsWith('ERROR_');
    const graphDisplayProps = {
        display: display?.stack?.length === 0 && displayIdx > 0 && !isErrorState
            ? snapshots[displayIdx - 1]
            : display,
        grayOut: display?.stack?.length === 0,
        idVec, varIdVec, showBindings,
        activeChain, choicePointMap, inactiveFrames, queryKey,
        lightMode,
    };

    // Disabled states for controls.
    const inputReady  = ready && query.trim().length > 0;
    const stepDisabled = !inputReady || (display?.atEnd === true && isAtLatest);

    // ── Scrubber ──────────────────────────────────────────────────────────────
    const scrubber = snapshots.length > 1 && (
        <div className="scrubber-track" style={{ flex: 1 }}>
            <input
                type="range"
                min={0}
                max={snapshots.length - 1}
                value={displayIdx}
                onChange={e => setDisplayIdx(Number(e.target.value))}
            />
            {snapshots.map((snap, i) => snap.solutionFound && (
                <div
                    key={i}
                    className="scrubber-dot"
                    style={{ left: `calc(7px + ${i / (snapshots.length - 1)} * (100% - 14px))` }}
                    title={`Solution at step ${i}`}
                />
            ))}
        </div>
    );

    return (
        <div className={`app-layout${lightMode ? ' light-mode' : ''}`}>

            {/* ── Main area: graph + floating panels ── */}
            <div className="app-main" ref={appMainRef}>
                <GraphView {...graphDisplayProps} fullscreen={true} />

                <CodePanel
                    clauses={clauses}
                    setClauses={setClauses}
                    query={query}
                    setQuery={setQuery}
                    presets={presets}
                    clauseError={clauseError}
                    lightMode={lightMode}
                    getMaxWidth={getMaxCodeWidth}
                    onClausesMount={(editor, monaco) => {
                        clausesEditorRef.current = editor;
                        monacoRef.current = monaco;
                    }}
                />

                <InfoPanel
                    display={display}
                    solutionSnaps={solutionSnaps}
                    offset={solutionOffset}
                    onOffsetChange={setSolutionOffset}
                    namedVars={namedVars}
                    idVec={idVec}
                    varIdVec={varIdVec}
                    inactiveFrames={inactiveFrames}
                    setInactiveFrames={setInactiveFrames}
                    showBindings={showBindings}
                    setShowBindings={setShowBindings}
                    showStack={showStack}
                    setShowStack={setShowStack}
                    showDecisionStack={showDecisionStack}
                    setShowDecisionStack={setShowDecisionStack}
                    showInternalErrors={showInternalErrors}
                    setShowInternalErrors={setShowInternalErrors}
                    tailCallOpt={tailCallOpt}
                    setTailCallOpt={setTailCallOpt}
                    lightMode={lightMode}
                    setLightMode={setLightMode}
                    sidebarRef={infoPanelSidebarRef}
                    activeChain={activeChain}
                    choicePointMap={choicePointMap}
                />

                {/* ── Floating play controls — bottom-left ── */}
                <div className="play-controls">
                    <button
                        onClick={handleRun}
                        disabled={!inputReady}
                        title="Initialise query (resets to step 0)"
                    >Run</button>
                    <button
                        onClick={handleRunAll}
                        disabled={!inputReady}
                        title="Run to completion"
                    >All</button>

                    <div className="play-controls-divider" />

                    <button
                        className={`play-btn-icon${isPlaying ? ' play-btn-active' : ''}`}
                        onClick={handlePlayToggle}
                        disabled={!inputReady}
                        title={isPlaying ? 'Pause' : 'Play'}
                    >{isPlaying ? '⏸' : '▶'}</button>
                    <button
                        className="play-btn-icon"
                        onClick={handleStep}
                        disabled={stepDisabled}
                        title="Step once"
                    >⏭</button>

                    <select
                        className="play-speed-select"
                        value={playSpeed}
                        onChange={e => setPlaySpeed(e.target.value)}
                        title="Playback speed"
                    >
                        <option value="slow">Slow</option>
                        <option value="normal">Normal</option>
                        <option value="fast">Fast</option>
                        <option value="max">Max</option>
                    </select>
                </div>
            </div>

            {/* ── Bottom bar: scrubber only ── */}
            <div className="app-bottom-bar">
                {scrubber}
                {snapshots.length > 1 && (
                    <span className="app-step-label">
                        {displayIdx}/{snapshots.length - 1}
                    </span>
                )}
                {status && <span className="app-status-text">{status}</span>}
            </div>

        </div>
    );
}

export default App;
